import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Flex } from "rebass/styled-components";
import { HiCheck, HiChevronDown } from "react-icons/hi";

type SelectVariant = "modal" | "overlay";

interface AspectRatioSelectProps {
  value: string;
  options: string[];
  onChange: (aspectRatio: string) => void;
  variant?: SelectVariant;
}

export const AspectRatioSelect = ({
  value,
  options,
  onChange,
  variant = "modal",
}: AspectRatioSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (option: string) => {
    setIsOpen(false);
    if (option !== value) onChange(option);
  };

  return (
    <Container ref={containerRef}>
      <Trigger
        type="button"
        $variant={variant}
        $open={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {value}
        <Chevron $open={isOpen}>
          <HiChevronDown size={16} />
        </Chevron>
      </Trigger>
      {isOpen && (
        <Menu $variant={variant}>
          {options.map((option) => (
            <Option
              key={option}
              $variant={variant}
              $selected={option === value}
              onClick={() => handleSelect(option)}
            >
              {option}
              {option === value && <HiCheck size={14} />}
            </Option>
          ))}
        </Menu>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: inline-block;
  position: relative;
`;

const Trigger = styled.button<{ $variant: SelectVariant; $open: boolean }>`
  align-items: center;
  background: ${({ $variant, theme }) =>
    $variant === "overlay" ? "rgba(255, 255, 255, 0.1)" : theme.colors.ned};
  border: 1px solid
    ${({ $variant, $open, theme }) =>
      $open
        ? $variant === "overlay"
          ? "#4ebd94"
          : theme.colors.envy
        : $variant === "overlay"
        ? "rgba(255, 255, 255, 0.25)"
        : theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ $variant, theme }) =>
    $variant === "overlay" ? "white" : theme.colors.ice};
  cursor: pointer;
  display: inline-flex;
  font-family: "Barlow", sans-serif;
  font-size: 14px;
  font-weight: 600;
  gap: 8px;
  min-width: 88px;
  justify-content: space-between;
  padding: 7px 10px 7px 14px;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${({ $variant, theme }) =>
      $variant === "overlay" ? "rgba(255, 255, 255, 0.6)" : theme.colors.chalk};
  }
`;

const Chevron = styled(Flex)<{ $open: boolean }>`
  align-items: center;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  transition: transform 0.15s;
`;

const Menu = styled.div<{ $variant: SelectVariant }>`
  backdrop-filter: blur(12px);
  background: ${({ $variant, theme }) =>
    $variant === "overlay" ? "rgba(0, 0, 0, 0.85)" : theme.colors.ned};
  border: 1px solid
    ${({ $variant, theme }) =>
      $variant === "overlay"
        ? "rgba(255, 255, 255, 0.25)"
        : theme.colors.chalkAlpha20};
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
  min-width: 100%;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  z-index: 20;
`;

const Option = styled(Flex)<{ $variant: SelectVariant; $selected: boolean }>`
  align-items: center;
  color: ${({ $variant, $selected, theme }) =>
    $selected
      ? $variant === "overlay"
        ? "#4ebd94"
        : theme.colors.envy
      : $variant === "overlay"
      ? "rgba(255, 255, 255, 0.8)"
      : theme.colors.chalk};
  cursor: pointer;
  font-family: "Barlow", sans-serif;
  font-size: 14px;
  font-weight: ${({ $selected }) => ($selected ? 700 : 500)};
  gap: 10px;
  justify-content: space-between;
  padding: 8px 12px 8px 14px;
  transition: background 0.1s;
  white-space: nowrap;

  &:hover {
    background: ${({ $variant, theme }) =>
      $variant === "overlay"
        ? "rgba(255, 255, 255, 0.12)"
        : theme.colors.chalkAlpha20};
  }
`;
